import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { parseQuery } from "../../utils/queryUtils";
import { Button, Col, Container, Form, InputGroup, Row } from "react-bootstrap";
import CodesContext from "../CodesContext";
import type { ElakePerhevapaanAjaltaResult } from "../../types/ElakePerhevapaanAjaltaResult";
import type { ElakePerhevapaanAjaltaModel } from "../../types/ElakePerhevapaanAjaltaModel";
import { type Feedback, scrollTo } from "../../utils/form-util";
import { useCodeList } from "../useCodeList";
import {
  type PensionEstimate,
  type YearConstants,
  yearConstantValue as yearConstantValueOriginal,
  pensionEstimateValue,
} from "../../types/Codes";
import { useValidation } from "../useValidation";
import ValidationContext, { type Validation } from "../ValidationContext";
import ValidationFeedback from "../ValidationFeedback";
import { trackEvent } from "../../utils/piwik-util";
import NumberInput from "../NumberInput";
import LargeSelectInput from "../LargeSelectInput";
import ResultsGroup from "../ResultsGroup";
import { rangeClosed } from "../../utils/array-utils";
import {
  calculate,
  calculateChartData,
  type CalculationContext,
  type ChartContext,
  type ChartData,
} from "../../calculation/ElakePerhevapaanAjaltaCalculation";
import CalculationResult from "../CalculationResult";
import { euroSumOrZero, toFiLocaleTwoDecimals } from "../../utils/numberUtils";
import { type ChartOptions, defaults } from "chart.js";
import { Line } from "react-chartjs-2";
import { useValidationOnLanguageChange } from "../useValidationOnLanguageChange";
import HelpTextAccordion from "../HelpTextAccordion";

const ElakePerhevapaanAjaltaCalculator: React.FC = () => {
  const yearConstants = useCodeList<YearConstants>("year-constants");
  const pensionEstimate = useCodeList<PensionEstimate>("pension-estimate");
  return (
    <CodesContext.Provider value={{ yearConstants, pensionEstimate }}>
      <ElakePerhevapaanAjaltaView />
    </CodesContext.Provider>
  );
};

const ElakePerhevapaanAjaltaView: React.FC = () => {
  const { t } = useTranslation();

  const { yearConstants, pensionEstimate } = useContext(CodesContext);
  const [model, setModel] = useState<ElakePerhevapaanAjaltaModel | null>(null);
  const [result, setResult] = useState<ElakePerhevapaanAjaltaResult | null>(
    null,
  );
  const [resultChart, setResultChart] = useState<ChartData | null>(null);
  const [missingCodeErrors, setMissingCodeErrors] = useState<Feedback[]>([]);
  const resultsSection = useRef<HTMLDivElement | null>(null);
  const { search } = useLocation();
  const query = parseQuery(search);
  // Allow specifying year for e.g. repeatable Robot testing and manual testing:
  const year = Number.parseInt(query.year) || new Date().getFullYear();
  const validation = useValidation();

  const yearConstantValue = useCallback(
    (constant: keyof YearConstants) =>
      yearConstantValueOriginal(year, constant, yearConstants),
    [year, yearConstants],
  );

  const calculationContext = useMemo(() => {
    if (!model) {
      return null;
    }
    let deductionCurrentYear = yearConstantValue(
      "vakuutusmaksuvahennysKuluvavuosi",
    );
    let pensionReasonFactor = yearConstantValue("elakkeenPerusteKerroin");
    let pensionReasonMinimumAllowance = yearConstantValue(
      "elakkeenPerusteMinimipaivarahasta",
    );
    let accruePercentage = yearConstantValue("karttumisProsentti");
    let lifetimeFactor = pensionEstimateValue(
      model.birthYear || 0,
      "ek",
      pensionEstimate,
    );
    return {
      deductionCurrentYear,
      pensionReasonFactor,
      pensionReasonMinimumAllowance,
      accruePercentage,
      lifetimeFactor,
    } as CalculationContext;
  }, [yearConstants, pensionEstimate, model]);

  useEffect(() => {
    if (model) {
      if (calculationContext) {
        setResult(calculate(model, calculationContext));
        let chartdata = calculateChartData(model, calculationContext, {
          translate: t,
        } as ChartContext);
        setResultChart(chartdata);
        scrollTo(resultsSection);
        resultsSection.current?.focus();
      } else {
        setMissingCodeErrors([
          { text: t("errorFeedback.missingCodeValuesForThisYear") },
        ]);
      }
    } else {
      setResult(null);
    }
  }, [model, calculationContext, t]);

  return (
    <>
      <Container className="calculator-container content-container">
        <h1>{t("elake-perhevapaan-ajalta-laskuri.calculatorTitle")}</h1>
        <p className="ingress">
          {t("elake-perhevapaan-ajalta-laskuri.ingress")}
        </p>
      </Container>
      <div className="form-container py-4">
        <Container className="content-container">
          <Row>
            <Col md={12}>
              <ElakePerhevapaanAjaltaForm
                year={year}
                onClear={() => setResult(null)}
                onSubmit={(event, model) => {
                  event.preventDefault();
                  setModel(model);
                }}
                validation={validation}
              />
            </Col>
          </Row>
          <Row>
            <ValidationFeedback
              className={"mt-5"}
              titleKey={`errorFeedback.internalErrorTitle`}
              feedback={missingCodeErrors}
            />
            {!missingCodeErrors.length &&
              !validation.validationFeedback.length && (
                <Col
                  id="results-region"
                  role="region"
                  aria-label={t(
                    "elake-perhevapaan-ajalta-laskuri.results.title",
                  )}
                  md={12}
                  className="mt-4"
                  ref={resultsSection}
                  tabIndex={-1}
                >
                  {result && (
                    <ResultsGroup
                      legend={t("elake-perhevapaan-ajalta-laskuri.results.title")}
                      className="results-container"
                    >
                      {!!result.parentalPensionOverall && (
                        <>
                          <CalculationResult
                            className="mb-0"
                            fieldName="elake-perhevapaan-ajalta-laskuri.results.accruementDuringFamilyLeave"
                          >
                            <>{euroSumOrZero(result.parentalPensionOverall)}</>
                          </CalculationResult>
                          {(!!result.pensionAllowance ||
                            !!result.pensionHomeCare) && (
                            <div className="mb-3">
                              {result.pensionAllowance !== undefined && (
                                <div>
                                  {t(
                                    "elake-perhevapaan-ajalta-laskuri.results.accruementDuringFamilyLeave.duringAllowancePeriod",
                                    {
                                      euros: toFiLocaleTwoDecimals(
                                        result.pensionAllowance,
                                      ),
                                    },
                                  )}
                                </div>
                              )}
                              {result.pensionHomeCare !== undefined && (
                                <div>
                                  {t(
                                    "elake-perhevapaan-ajalta-laskuri.results.accruementDuringFamilyLeave.duringHomeCarePeriod",
                                    {
                                      euros: toFiLocaleTwoDecimals(
                                        result.pensionHomeCare,
                                      ),
                                    },
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                      {!!result.pensionSalary && (
                        <CalculationResult fieldName="elake-perhevapaan-ajalta-laskuri.results.accruementPotentialSalary">
                          <>{euroSumOrZero(result.pensionSalary)}</>
                        </CalculationResult>
                      )}
                      {!!resultChart && (
                        <div className="chart-container">
                          <ElakePerhevapaanAjaltaChart data={resultChart} />
                        </div>
                      )}
                      <div className="mb-2">
                        {t(
                          "elake-perhevapaan-ajalta-laskuri.results.explanation",
                        )}
                      </div>
                    </ResultsGroup>
                  )}
                </Col>
              )}
          </Row>
        </Container>
      </div>
    </>
  );
};

interface FormProps {
  year: number;
  onSubmit: (
    event: React.SyntheticEvent<HTMLFormElement>,
    model: ElakePerhevapaanAjaltaModel,
  ) => void;
  onClear: () => void;
  validation: Validation;
}

const ElakePerhevapaanAjaltaForm: React.FC<FormProps> = ({
  year,
  onSubmit,
  onClear,
  validation,
}) => {
  const { t } = useTranslation();
  const topSection = useRef<HTMLDivElement | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const errorSummary = useRef<HTMLDivElement>(null);
  useValidationOnLanguageChange(
    formRef,
    validation.validateForm,
    validation.validated,
  );

  const [formModel, setFormModel] = useState<ElakePerhevapaanAjaltaModel>({});

  const handleSubmit = (event: React.SyntheticEvent<HTMLFormElement>) => {
    const validateResult = validation.validate(event);
    if (validateResult) {
      onSubmit(event, formModel);
    } else {
      onClear();
      // Move focus to error summary
      if (errorSummary.current) {
        errorSummary.current.focus();
        setTimeout(() => {
          errorSummary.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }, 100);
      } else {
        scrollTo(topSection);
      }
    }
  };

  const birthYearOptions = rangeClosed(1973, year - 18).map((y) => ({
    value: y,
    label: y + "",
  }));
  const allowanceDurationOptions = [
    ...rangeClosed(0, 10).map((o) => ({ value: o, label: o + "" })),
    ...rangeClosed(11, 13).map((o) => ({
      value: o,
      label: `${o} ${t(
        "elake-perhevapaan-ajalta-laskuri.allowanceDuration.specialCase",
      )}`,
    })),
  ];
  const homeCareDurationOptions = rangeClosed(0, 30).map((o) => ({
    value: o,
    label: o + "",
  }));

  return (
    <ValidationContext.Provider value={validation}>
      <div ref={topSection}></div>
      <Form
        ref={formRef}
        noValidate
        validated={validation.validated}
        onSubmit={handleSubmit}
        className="calculator-form"
        id={"perhevapaa"}
      >
        <ValidationFeedback
          feedback={validation.validationFeedback}
          ref={errorSummary}
        />
        <p className="required-fields-info">
          <span className="required">*</span>&nbsp;
          {t("requiredFieldsInfo")}
        </p>
        <Row>
          <Col md={12}>
            <LargeSelectInput
              required={true}
              fieldName="elake-perhevapaan-ajalta-laskuri.birthYear"
              showValidation={validation.validated}
              label={
                <span className="error-label">
                  {t("elake-perhevapaan-ajalta-laskuri.birthYear.label")}
                  <span className="required">*</span>
                </span>
              }
              helpTextAccordion={
                <HelpTextAccordion fieldName="elake-perhevapaan-ajalta-laskuri.birthYear" />
              }
              options={birthYearOptions}
              value={
                formModel.birthYear !== undefined
                  ? birthYearOptions.find(
                      (o) => o.value === formModel.birthYear,
                    ) || null
                  : null
              }
              setValue={(selected) =>
                setFormModel((fm) => ({ ...fm, birthYear: selected?.value }))
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <InputGroup
              hasValidation
              className={`${
                validation.validated ? "was-validated" : "was-not-validated"
              }`}
            >
              <NumberInput
                fieldName="elake-perhevapaan-ajalta-laskuri.salaryPreviousYear"
                initialValue={formModel.salaryPreviousYear}
                required
                min={0}
                max={999999999}
                onlyInts
                onValidValueChange={(salaryPreviousYear) =>
                  setFormModel((fm) => ({ ...fm, salaryPreviousYear }))
                }
              />

              <LargeSelectInput
                fieldName="elake-perhevapaan-ajalta-laskuri.allowanceDuration"
                options={allowanceDurationOptions}
                searchable={true}
                label={
                  <span className="error-label">
                    {t(
                      "elake-perhevapaan-ajalta-laskuri.allowanceDuration.label",
                    )}
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="elake-perhevapaan-ajalta-laskuri.allowanceDuration" />
                }
                value={formModel.allowanceDuration}
                setValue={(selected) =>
                  setFormModel((fm) => ({
                    ...fm,
                    allowanceDuration: selected?.value,
                  }))
                }
              />

              <LargeSelectInput<number>
                fieldName="elake-perhevapaan-ajalta-laskuri.homeCareDuration"
                searchable={true}
                label={
                  <span className="error-label">
                    {t(
                      "elake-perhevapaan-ajalta-laskuri.homeCareDuration.label",
                    )}
                  </span>
                }
                helpTextAccordion={
                  <HelpTextAccordion fieldName="elake-perhevapaan-ajalta-laskuri.homeCareDuration" />
                }
                options={homeCareDurationOptions}
                value={formModel.homeCareDuration}
                setValue={(selected) =>
                  setFormModel((fm) => ({
                    ...fm,
                    homeCareDuration: selected?.value,
                  }))
                }
              />
            </InputGroup>
          </Col>
        </Row>
        <Row>
          <Col sm={6} className="mt-3">
            <Button
              type="submit"
              aria-controls="results-region"
              onClick={() => {
                trackEvent(
                  "Button",
                  t("elake-perhevapaan-ajalta-laskuri.calculate"),
                );
              }}
            >
              {t("elake-perhevapaan-ajalta-laskuri.calculate")}
            </Button>
          </Col>
        </Row>
      </Form>
    </ValidationContext.Provider>
  );
};

interface ChartProps {
  data: ChartData;
}

const ElakePerhevapaanAjaltaChart: React.FC<ChartProps> = ({ data }) => {
  const { t } = useTranslation();

  defaults.font.size = 14;
  defaults.color = "#4e5965";
  const options = useMemo(
    () =>
      ({
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 20,
          },
        },
        elements: {
          point: {
            radius: 3,
          },
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: t("elake-perhevapaan-ajalta-laskuri.chart.month"),
            },
            ticks: {
              beginAtZero: true,
            },
          },
          y: {
            display: true,
            title: {
              display: true,
              text: t("elake-perhevapaan-ajalta-laskuri.chart.unit"),
            },
            ticks: {
              beginAtZero: true,
            },
          },
        },
        plugins: {
          legend: {
            position: "bottom",
            align: "center",
            useLineStyle: true,
            onClick: () => {},
            labels: {
              boxHeight: 0,
              fontSize: 16,
              padding: 15,
            },
          },
          tooltip: {
            enabled: true,
            callbacks: {
              title: (context: any) => {
                return context[0].dataset.label;
              },
              label: (context: any) => {
                return t(
                  "elake-perhevapaan-ajalta-laskuri.chart.tooltipLabel",
                  {
                    month: context.parsed.x,
                    euros: toFiLocaleTwoDecimals(context.parsed.y),
                  },
                );
              },
            },
          },
          datalabels: {
            formatter: (value: any) =>
              t("elake-perhevapaan-ajalta-laskuri.chart.datalabel", {
                euros: euroSumOrZero(value),
              }),
            display: (context: any) =>
              context.dataIndex === context.dataset.data.length - 1,
            align: 225,
            font: {
              weight: "bold",
            },
          },
        },
      }) as ChartOptions,
    [t],
  );

  return (
    <>
      <Line options={options as any} data={data} />
      <div className="sr-only">
        <table>
          <caption>
            {t("elake-perhevapaan-ajalta-laskuri.chart.tableCaption")}
          </caption>
          <thead>
            <tr>
              <th>{t("elake-perhevapaan-ajalta-laskuri.chart.month")}</th>
              <th>
                {t(
                  "elake-perhevapaan-ajalta-laskuri.chart.pensionFromParentalLeave",
                )}
              </th>
              <th>
                {t("elake-perhevapaan-ajalta-laskuri.chart.pensionFromSalary")}
              </th>
            </tr>
          </thead>
          <tbody>{getScreenreaderDatatableRows(data, t)}</tbody>
        </table>
      </div>
    </>
  );
};

const getScreenreaderDatatableRows = (data: ChartData, translate: any) => {
  let result: React.ReactElement[] = [];
  for (let i = 0; i < data.labels.length; i++) {
    result.push(
      <tr key={i}>
        <td>{`${i} ${translate(
          "elake-perhevapaan-ajalta-laskuri.chart.month",
        )}`}</td>
        <td>
          {translate("elake-perhevapaan-ajalta-laskuri.chart.datalabel", {
            euros: data.datasets[0].data[i],
          })}
        </td>
        <td>
          {translate("elake-perhevapaan-ajalta-laskuri.chart.datalabel", {
            euros: data.datasets[1].data[i],
          })}
        </td>
      </tr>,
    );
  }
  return result;
};

export default ElakePerhevapaanAjaltaCalculator;
